/**
 * @file Expiration Settings Step Component
 * @description Step for configuring CloudTrail and Athena expiration settings for CIEM
 * (Cloud Infrastructure Entitlement Management) AWS integrations.
 * Validates that all expiration values are positive integers before allowing progression.
 */

import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';

import { validateNumber } from '../../../../../../../../lib/inputValidator';
import { StepComponentProps } from '../StepperModal';

/**
 * Default expiration values in days, used when no prior data exists.
 */
const DEFAULTS = {
  cloudTrailExpiration: 180,
  cloudTrailNonCurrent: 14,
  athenaResultsExpiration: 90,
};

/**
 * Returns true if the value is a positive integer (> 0).
 */
const isPositiveNumber = (value: string): boolean => {
  if (value.trim() === '') return false;
  if (!/^\d+$/.test(value.trim())) return false;
  const validation = validateNumber(value, {
    allowNegative: false,
    maxLength: 6,
  });
  if (!validation.isValid) return false;
  const num = Number(value);
  return Number.isInteger(num) && num > 0;
};

/**
 * ExpirationSettingsStep - Configures CloudTrail and Athena expiration settings
 *
 * Used in CIEM AWS integrations to set data retention and expiration policies
 * for CloudTrail logs and Athena query results stored in S3.
 *
 * Features:
 * - Three numeric input fields with sensible defaults
 * - Real-time positive number validation
 * - Inline error messages per field
 * - Next button enabled only when all fields contain valid positive numbers
 *
 * Validation Rules:
 * - All fields are required
 * - All values must be positive numbers (> 0)
 *
 * @component
 * @param {StepComponentProps} props - Step component props
 * @returns {React.ReactElement} Form for collecting and validating expiration settings
 */
const ExpirationSettingsStep: React.FC<StepComponentProps> = ({
  description,
  data,
  onValidationChange,
  onNext,
  onBack,
  loading,
  allowBack,
}) => {
  // Initialise with existing data or sensible defaults so state matches displayed value
  const [cloudTrailExpiration, setCloudTrailExpiration] = useState(
    data.cloudTrailExpiration
      ? String(data.cloudTrailExpiration)
      : String(DEFAULTS.cloudTrailExpiration),
  );
  const [cloudTrailNonCurrent, setCloudTrailNonCurrent] = useState(
    data.cloudTrailNonCurrent
      ? String(data.cloudTrailNonCurrent)
      : String(DEFAULTS.cloudTrailNonCurrent),
  );
  const [athenaResultsExpiration, setAthenaResultsExpiration] = useState(
    data.athenaResultsExpiration
      ? String(data.athenaResultsExpiration)
      : String(DEFAULTS.athenaResultsExpiration),
  );

  // Per-field validation error messages
  const [validationErrors, setValidationErrors] = useState<{
    cloudTrailExpiration?: string;
    cloudTrailNonCurrent?: string;
    athenaResultsExpiration?: string;
  }>({});

  /**
   * Returns true only when all three fields contain positive numbers.
   * Used both in the useEffect (to notify parent) and before form submission.
   */
  const allFieldsValid = (): boolean =>
    isPositiveNumber(cloudTrailExpiration) &&
    isPositiveNumber(cloudTrailNonCurrent) &&
    isPositiveNumber(athenaResultsExpiration);

  /**
   * Runs full field-level validation and populates inline error messages.
   * @returns {boolean} True if all validations pass
   */
  const validateExpirationSettings = (): boolean => {
    const errors: {
      cloudTrailExpiration?: string;
      cloudTrailNonCurrent?: string;
      athenaResultsExpiration?: string;
    } = {};

    if (!isPositiveNumber(cloudTrailExpiration)) {
      errors.cloudTrailExpiration =
        'CloudTrail Expiration must be a positive number';
    }
    if (!isPositiveNumber(cloudTrailNonCurrent)) {
      errors.cloudTrailNonCurrent =
        'CloudTrail Non-Current must be a positive number';
    }
    if (!isPositiveNumber(athenaResultsExpiration)) {
      errors.athenaResultsExpiration =
        'Athena Results Expiration must be a positive number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Notify parent of validation state whenever any field changes.
   * Parent uses this to enable/disable the wizard's Next button.
   */
  useEffect(() => {
    onValidationChange(allFieldsValid());
    // eslint-disable-next-line
  }, [cloudTrailExpiration, cloudTrailNonCurrent, athenaResultsExpiration]);

  /**
   * Handles form submission.
   * Runs full validation with inline errors before advancing to the next step.
   */
  const handleNext = () => {
    if (!validateExpirationSettings()) {
      return;
    }
    onNext({
      ...data,
      cloudTrailExpiration,
      cloudTrailNonCurrent,
      athenaResultsExpiration,
    });
  };

  return (
    <div className="step-content-wrapper">
      {/* Zone 1 – Step description (pinned header) */}
      <div className="step-description-header">
        <h5>{description ?? 'Expiration Settings'}</h5>
      </div>

      {/* Zone 2 – Scrollable form body */}
      <div className="step-form-body">
        {/* CloudTrail Expiration */}
        <FormGroup>
          <Label for="cloudtrail_expiration">
            CloudTrail Log Retention (Days)*
          </Label>
          <Input
            type="text"
            id="cloudtrail_expiration"
            placeholder="Enter CloudTrail Log Retention (Days)"
            value={cloudTrailExpiration}
            onChange={(e) => {
              const val = e.target.value;
              setCloudTrailExpiration(val);
              if (!isPositiveNumber(val)) {
                setValidationErrors((prev) => ({
                  ...prev,
                  cloudTrailExpiration:
                    'CloudTrail Log Retention must be a positive number',
                }));
              } else {
                setValidationErrors((prev) => ({
                  ...prev,
                  cloudTrailExpiration: undefined,
                }));
              }
            }}
            disabled={loading}
            className={
              validationErrors.cloudTrailExpiration
                ? 'input-error-highlight'
                : ''
            }
          />
          {validationErrors.cloudTrailExpiration && (
            <div className="invalid-feedback d-block text-danger">
              {validationErrors.cloudTrailExpiration}
            </div>
          )}
        </FormGroup>

        {/* CloudTrail Non-Current */}
        <FormGroup>
          <Label for="cloudTrailNonCurrent">
            Previous Log Versions Retention (Days)*
          </Label>
          <Input
            type="text"
            id="cloudTrailNonCurrent"
            placeholder="Enter Previous Log Versions Retention (Days)"
            value={cloudTrailNonCurrent}
            onChange={(e) => {
              const val = e.target.value;
              setCloudTrailNonCurrent(val);
              if (!isPositiveNumber(val)) {
                setValidationErrors((prev) => ({
                  ...prev,
                  cloudTrailNonCurrent:
                    'Previous Log Versions Retention must be a positive number',
                }));
              } else {
                setValidationErrors((prev) => ({
                  ...prev,
                  cloudTrailNonCurrent: undefined,
                }));
              }
            }}
            disabled={loading}
            className={
              validationErrors.cloudTrailNonCurrent
                ? 'input-error-highlight'
                : ''
            }
          />
          {validationErrors.cloudTrailNonCurrent && (
            <div className="invalid-feedback d-block text-danger">
              {validationErrors.cloudTrailNonCurrent}
            </div>
          )}
        </FormGroup>

        {/* Athena Results Expiration */}
        <FormGroup>
          <Label for="athenaResultsExpiration">
            Athena Query Results Retention (Days)*
          </Label>
          <Input
            type="text"
            id="athenaResultsExpiration"
            placeholder="Enter Athena Query Results Retention (Days)"
            value={athenaResultsExpiration}
            onChange={(e) => {
              const val = e.target.value;
              setAthenaResultsExpiration(val);
              if (!isPositiveNumber(val)) {
                setValidationErrors((prev) => ({
                  ...prev,
                  athenaResultsExpiration:
                    'Athena Query Results Retention must be a positive number',
                }));
              } else {
                setValidationErrors((prev) => ({
                  ...prev,
                  athenaResultsExpiration: undefined,
                }));
              }
            }}
            disabled={loading}
            className={
              validationErrors.athenaResultsExpiration
                ? 'input-error-highlight'
                : ''
            }
          />
          {validationErrors.athenaResultsExpiration && (
            <div className="invalid-feedback d-block text-danger">
              {validationErrors.athenaResultsExpiration}
            </div>
          )}
        </FormGroup>
      </div>

      {/* Zone 3 – Action buttons (pinned footer) */}
      <div
        className={`step-action-footer d-flex ${
          allowBack ? 'justify-content-between' : 'justify-content-end'
        }`}
      >
        {allowBack && (
          <Button color="secondary" onClick={onBack} disabled={loading}>
            Back
          </Button>
        )}
        <Button
          color="primary"
          onClick={handleNext}
          disabled={!allFieldsValid() || loading}
          className={allowBack ? '' : 'ml-auto'}
        >
          {loading ? 'Validating...' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default ExpirationSettingsStep;
